/**
 * Runtime schemas for every API response (docs/api-contract.md).
 * These are the single source of truth for frontend types: `type X = z.infer<typeof X>`.
 * Unknown extra fields are stripped; missing/invalid fields raise ApiSchemaError.
 */
import { z } from "zod";

// No code generation: Bason's CSP forbids eval, and zod's probe for it (`new Function`) is reported as a CSP
// violation (Firefox logs it as an error on every screen; found in the cross-browser run, D-32). Validation works
// the same without it.
z.config({ jitless: true });

const num = z.number();
const nnum = z.number().nullable();
const nstr = z.string().nullable();

export const SURFACES = ["paved", "paved_rough", "compacted", "gravel", "dirt", "trail", "unknown"] as const;
export const Surface = z.enum(SURFACES);
export type Surface = z.infer<typeof Surface>;

export const Climb = z.object({
  start_km: num,
  end_km: num,
  length_km: num,
  gain_m: num,
  avg_grade_pct: num,
  max_grade_pct: num,
  top_ele_m: num,
  category: z.enum(["HC", "1", "2", "3", "4"]),
  difficulty: num,
  unpaved_pct: num,
});
export type Climb = z.infer<typeof Climb>;

export const Segment = z.object({
  from_km: num,
  to_km: num,
  surface: Surface,
  highway: nstr,
  inferred: z.boolean(),
});
export type Segment = z.infer<typeof Segment>;

export const Analysis = z.object({
  distance_km: num,
  ascent_m: num,
  descent_m: num,
  min_ele_m: num,
  max_ele_m: num,
  climbing_density_m_per_km: num,
  surface_km: z.partialRecord(Surface, num).nullable(), // older analyses may lack a class
  unpaved_pct: nnum,
  surface_confidence_pct: nnum,
  segments: z.array(Segment).nullable(),
  climbs: z.array(Climb),
  gradient_hist: z.array(z.object({ bucket: z.string(), km: num })),
  steep_unpaved_km: nnum,
  hike_a_bike_km: nnum,
  profile: z.array(z.tuple([num, num, Surface.nullable()])),
  elevation_source: z.string(),
  surface_source: nstr.optional().default(null),
});
export type Analysis = z.infer<typeof Analysis>;

export const SESSION_KINDS = [
  "rest",
  "endurance",
  "long",
  "back_to_back",
  "recovery",
  "tempo_climbs",
  "sweet_spot",
  "ftp_test",
  "hockey",
  "sport",
  "strength",
  "race",
  "custom",
] as const;
export const SessionKind = z.enum(SESSION_KINDS);
export type SessionKind = z.infer<typeof SessionKind>;
export const SESSION_STATUSES = ["planned", "done", "skipped", "modified"] as const;

/** Day forecast (Open-Meteo): routes and the next 7 days of sessions. */
export const Weather = z.object({
  tmin: nnum,
  tmax: nnum,
  precip_mm: nnum,
  precip_prob: nnum,
  wind_max_kmh: nnum,
  wind_dir_deg: nnum.optional(),
  sunrise: nstr,
  sunset: nstr,
});
export type Weather = z.infer<typeof Weather>;

export const SKIP_REASONS = [
  "sick",
  "injury",
  "tired",
  "work",
  "family",
  "travel",
  "weather",
  "other",
] as const;
export const SkipReasonKey = z.enum(SKIP_REASONS);

export const PlannedSession = z.object({
  id: num,
  day: z.string(),
  phase: z.string(),
  kind: SessionKind,
  sport: z.string(),
  target_hours: num,
  target_load: num,
  target_ascent_m: nnum,
  min_unpaved_pct: nnum,
  hr_cap: nnum,
  power_low_w: nnum.optional().default(null),
  power_high_w: nnum.optional().default(null),
  description: z.string(),
  status: z.enum(SESSION_STATUSES),
  source: z.enum(["plan", "user", "adaptation"]),
  activity_id: nstr,
  skip_reason: SkipReasonKey.nullable().optional().default(null),
  skip_note: nstr.optional().default(null),
  garmin_workout_id: nstr.optional().default(null), // set once sent to the Garmin calendar
  weather: Weather.nullable().optional().default(null),
  weather_warnings: z.array(z.string()).optional().default([]),
});
export type PlannedSession = z.infer<typeof PlannedSession>;

export const RouteSummary = z.object({
  id: z.string(),
  day: z.string(),
  rank: num,
  title: z.string(),
  status: z.enum(["suggested", "chosen", "ridden", "dismissed"]),
  distance_km: num,
  ascent_m: num,
  est_hours: num,
  unpaved_pct: nnum,
  novelty_pct: nnum,
  score: num,
  reason: z.string(),
  notes: z.array(z.string()),
  start: z.object({ name: z.string(), lat: num, lon: num }).nullable(),
  finish: z.object({ name: z.string(), lat: num, lon: num }).nullable(),
  weather: Weather.nullable(),
  planned_session_id: nnum,
  garmin_course_id: nstr.optional().default(null), // set once saved as a Garmin course
  imported: z.boolean().optional().default(false), // the athlete's own route (upload, Komoot, chat)
});
export type RouteSummary = z.infer<typeof RouteSummary>;

export const RouteDetail = RouteSummary.extend({
  geometry: z.array(z.tuple([num, num, num])),
  analysis: Analysis,
  // Komoot: the route's own Komoot page, or Komoot's planner with its waypoints (Gravel, known ways)
  komoot_url: nstr.optional().default(null),
});

// GET /komoot/tours: the athlete's planned Komoot routes
export const KomootTour = z.object({
  id: z.string(),
  name: z.string(),
  sport: nstr,
  distance_km: num,
  ascent_m: num,
  duration_h: num,
  date: nstr,
  start: z.object({ lat: num, lon: num }).nullable(),
  imported_for: nstr,
});
export type KomootTour = z.infer<typeof KomootTour>;
// GET /komoot/tours/{id}/preview?day=: what "Use" would save, written nowhere (RD-27a)
export const KomootPreview = z.object({
  tour_id: z.string(),
  name: z.string(),
  day: z.string(),
  distance_km: num,
  ascent_m: num,
  est_hours: num,
  unpaved_pct: nnum,
  geometry: z.array(z.tuple([num, num, num])),
  analysis: Analysis.nullable(),
  plan: z.object({ hours: num, ascent_m: nnum }).nullable(), // the day's planned ride, if any
  diff: z.object({ minutes: num, ascent_m: nnum }).nullable(), // route minus plan
});
export type KomootPreview = z.infer<typeof KomootPreview>;
export const KomootLoginResult = z.object({ status: z.literal("connected"), display_name: z.string() });
export type RouteDetail = z.infer<typeof RouteDetail>;

/** What the A race demands; the plan and the index scale to it (backend planning/profile.py). */
export const RaceProfile = z.object({
  preset: nstr,
  name: z.string(),
  short: z.string(),
  distance_km: num,
  elevation_m: num,
  unpaved: num,
  days: num,
  surface: z.enum(["gravel", "road", "mtb"]),
  heat: z.boolean(),
  density_m_per_km: num,
  multi_day: z.boolean(),
  race_hours: num,
});
export type RaceProfile = z.infer<typeof RaceProfile>;
export const RacePreset = RaceProfile.extend({ id: z.string(), source: z.string() });
export type RacePreset = z.infer<typeof RacePreset>;

export const Goal = z.object({
  id: z.string(),
  name: z.string(),
  event_date: z.string(),
  distance_km: nnum,
  elevation_m: nnum,
  target_days: nnum,
  is_primary: z.boolean(),
  kind: z.enum(["A", "B", "C"]),
  days: num.optional().default(1),
  location: nstr.optional().default(null),
  lat: nnum.optional().default(null),
  lon: nnum.optional().default(null),
  url: nstr.optional().default(null),
  registered: z.boolean().optional().default(false),
  catalog_id: nstr.optional().default(null),
  notes: z.string().optional().default(""),
  days_to_go: nnum.optional(), // null for "No race yet" (no countdown)
  placeholder: z.boolean().optional().default(false), // "No race yet": the date is only the plan's horizon
  profile: z.record(z.string(), z.unknown()).nullable().optional().default(null),
  race: RaceProfile.nullable().optional().default(null),
  daily_km: nnum.optional().default(null),
  daily_m: nnum.optional().default(null),
});
export type Goal = z.infer<typeof Goal>;

export const Me = z.object({
  user: z.object({ id: z.string(), name: z.string(), email: z.string(), timezone: z.string() }),
  onboarded: z.boolean().optional().default(true),
  /** daily health data is stored and used (health-data consent) */
  health_data: z.boolean().optional().default(true),
  /** the current terms are accepted; false → the "Terms of use" screen first (D-39) */
  terms_accepted: z.boolean().optional().default(true),
  /** what this server offers (0.9.10): the AI coach, the official Garmin link, route suggestions */
  features: z
    .object({ ai: z.boolean(), garmin: z.boolean(), routes: z.boolean() })
    .optional()
    .default({ ai: false, garmin: false, routes: true }),
  goal: Goal.nullable(),
  thresholds: z.object({
    lthr: nnum,
    max_hr: nnum,
    resting_hr: nnum,
    ftp_w: nnum,
    valid_from: nstr,
    /** LTHR is the neutral default or 0.9 × max HR, not measured (0.9.10) */
    estimated: z.boolean().optional().default(false),
  }),
  zones: z.object({
    names: z.array(z.string()),
    bounds: z.array(num),
    lt1: num,
    power: z
      .object({ names: z.array(z.string()), bounds_w: z.array(num) })
      .nullable()
      .optional(),
  }),
});
export type Me = z.infer<typeof Me>;

export const Week = z.object({
  week_start: z.string(),
  phase: nstr,
  is_recovery: z.boolean(),
  planned_hours: num,
  planned_load: num,
  actual_hours: num,
  cycling_hours: num,
  actual_load: num,
  distance_km: num,
  ascent_m: num,
  z_low_h: num,
  z_mid_h: num,
  z_high_h: num,
  longest_h: num,
  sessions_planned: num,
  sessions_done: num,
  compliance_pct: nnum,
  outdoor_km: num.optional().default(0),
  unpaved_km: num.optional().default(0),
  unpaved_pct: nnum.optional().default(null), // share of outdoor km with a matched surface
  unmatched_km: num.optional().default(0), // ride km with no known surface: indoor or not matched yet (RD-14)
  unmatched_h: num.optional().default(0), // hours of those rides (a trainer without speed has no km) (RD-14)
  no_hr_h: num.optional().default(0), // hours without heart-rate zones, every sport like the zone hours (RD-14)
  planned_cycling_hours: num.optional().default(0),
  has_b2b: z.boolean().optional().default(false),
  // deprecated aliases of actual_hours / actual_load, kept for the Claude connector's get_weekly (contract)
  hours: num.optional(),
  load: num.optional(),
});
export type Week = z.infer<typeof Week>;

const Adjustment = z.object({
  id: num,
  created_at: z.string(),
  kind: z.string(),
  reason: z.string(),
});

export const Status = z.object({
  today: z.string(),
  features: z.object({ ai: z.boolean(), garmin: z.boolean(), routes: z.boolean() }).optional(),
  user: z.object({ name: z.string() }),
  // the full goal (the same shape as GET /goals) with the countdown (G2: the subset dropped the rest)
  goal: Goal.nullable(),
  fitness: z.object({ ctl: num, atl: num, tsb: num, ctl_target: nnum }),
  phase: z
    .object({
      name: z.string(),
      week_in_phase: num,
      weeks_in_phase: num,
      target_weekly_hours: num,
      focus: z.string(),
      /** plain warnings about the plan (e.g. free time far below the race), 0.9.10 */
      warnings: z.array(z.string()).optional().default([]),
    })
    .nullable(),
  this_week: Week,
  latest: z.object({
    weight_kg: nnum,
    weight_at: nstr,
    bp_sys: nnum,
    bp_dia: nnum,
    bp_at: nstr,
    resting_hr: nnum,
    hrv_ms: nnum,
    sleep_s: nnum,
  }),
  readiness: z.object({
    longest_ride_h_90d: num,
    best_b2b_hours_90d: num,
    weekly_hours_avg_4w: num,
    target_weekly_hours_now: nnum,
    max_climbing_day_m_90d: num,
    notes: z.array(z.string()),
  }),
  today_sessions: z.array(PlannedSession),
  tomorrow_sessions: z.array(PlannedSession),
  // declared further down (readiness/insights block) → lazy references
  daily_readiness: z
    .lazy(() => Readiness)
    .nullable()
    .optional()
    .default(null),
  last_activity: z
    .lazy(() =>
      ActivitySummary.extend({ insights: Insights, checkin: Checkin.nullable().optional().default(null) }),
    )
    .nullable()
    .optional()
    .default(null),
  amr_fitness: z
    .lazy(() => AmrFitnessToday)
    .nullable()
    .optional()
    .default(null),
  checkin_today: z
    .lazy(() => Checkin)
    .nullable()
    .optional()
    .default(null),
  inbox: z
    .lazy(() => z.array(InboxItem))
    .optional()
    .default([]),
  adjustments: z.array(Adjustment),
  connections: z.array(z.object({ provider: z.string(), status: z.string(), last_sync_at: nstr })),
  sync_warning: nstr.optional().default(null),
});
export type Status = z.infer<typeof Status>;

// ---------------------------------------------------------------- v1.2: readiness, insights, all-day HR
export const ReadinessComponent = z.object({
  key: z.string(),
  label: z.string(),
  value: z.union([z.number(), z.string()]).nullable(),
  unit: z.string(),
  baseline: z.union([z.number(), z.string()]).nullable(),
  impact: num,
  note: z.string(),
});
export const Readiness = z.object({
  day: z.string(),
  score: nnum,
  level: z.enum(["good", "caution", "low", "unknown"]),
  advice: z.string(),
  components: z.array(ReadinessComponent),
  device: z.object({
    readiness_score: nnum,
    readiness_level: nstr,
    recovery_time_h: nnum,
    training_status: nstr,
    acute_load: nnum,
    chronic_load: nnum,
    acwr: nnum,
    hrv_status: nstr,
    load_balance_feedback: nstr,
    vo2max_cycling: nnum,
  }),
});
export type Readiness = z.infer<typeof Readiness>;

export const Insights = z.object({
  verdict: z.enum(["on_target", "too_hard", "too_short", "too_long", "unplanned"]),
  compliance_pct: nnum,
  items: z.array(
    z.object({
      label: z.string(),
      planned: z.union([z.number(), z.string()]).nullable(),
      actual: z.union([z.number(), z.string()]).nullable(),
      unit: z.string(),
      ok: z.boolean().nullable(),
    }),
  ),
  notes: z.array(z.string()),
  zones: z.object({ low_h: num, mid_h: num, high_h: num }),
  lt1: num,
});
export type Insights = z.infer<typeof Insights>;

export const HeartRateDay = z.object({
  day: z.string(),
  samples: z.array(z.tuple([num, num])),
  resting_hr: nnum,
  min_hr: nnum,
  max_hr: nnum,
});
export type HeartRateDay = z.infer<typeof HeartRateDay>;

export const FitnessPoint = z.object({
  day: z.string(),
  load: num,
  ctl: num,
  atl: num,
  tsb: num,
  ctl_target: nnum,
  ctl_cycling: nnum.optional().default(null), // bike-only fitness (hockey left out)
});
export type FitnessPoint = z.infer<typeof FitnessPoint>;

export const ActivitySummary = z.object({
  id: z.string(),
  name: z.string(),
  sport: z.string(),
  start_time: z.string(),
  elapsed_s: num,
  moving_s: nnum,
  distance_m: nnum,
  ascent_m: nnum,
  avg_hr: nnum,
  max_hr: nnum,
  avg_power: nnum,
  hr_tss: nnum,
  trimp: nnum,
  zone_seconds: z.array(num).nullable(),
  hr_drift_pct: nnum,
  te_aerobic: nnum,
  te_anaerobic: nnum,
  source: z.string(),
  tss: nnum.optional().default(null),
  tss_source: z.enum(["power", "hr"]).nullable().optional().default(null),
  np_w: nnum.optional().default(null),
  intensity_factor: nnum.optional().default(null),
  platform: nstr.optional().default(null),
  virtual_route: nstr.optional().default(null),
  calories: nnum.optional().default(null),
  garmin: z
    .record(z.string(), z.union([z.number(), z.string()]))
    .nullable()
    .optional()
    .default(null),
  weather: z
    .object({
      temp_c: num,
      temp_min_c: nnum,
      temp_max_c: nnum,
      precip_mm: nnum,
      wind_kmh: nnum,
      wind_dir_deg: nnum,
      source: z.string(),
    })
    .nullable()
    .optional()
    .default(null),
  unpaved_km: nnum.optional().default(null),
});
export type ActivitySummary = z.infer<typeof ActivitySummary>;

export const ActivityPage = z.object({ items: z.array(ActivitySummary), total: num });

const nArr = z.array(z.number().nullable()).nullable().optional();
export const ActivityDetail = ActivitySummary.extend({
  stream: z.object({
    t: z.array(num),
    hr: nArr,
    speed: nArr,
    alt: nArr,
    dist: nArr,
    lat: nArr,
    lon: nArr,
    power: nArr,
    cadence: nArr,
  }),
  analysis: Analysis.nullable(),
  planned_session: PlannedSession.nullable(),
  // the route chosen for that day (Komoot, GPX or a suggestion) next to what was ridden
  planned_route: z
    .object({
      id: z.string(),
      title: z.string(),
      source: z.enum(["komoot", "gpx", "suggestion"]),
      planned: z.object({ distance_km: num, ascent_m: num, unpaved_pct: nnum }),
      ridden: z.object({ distance_km: num, ascent_m: num, unpaved_pct: nnum }),
    })
    .nullable()
    .optional()
    .default(null),
  insights: Insights.nullable().optional().default(null),
  checkin: z
    .lazy(() => Checkin)
    .nullable()
    .optional()
    .default(null),
});
export type ActivityDetail = z.infer<typeof ActivityDetail>;

export const Phase = z.object({
  name: z.string(),
  start: z.string(),
  end: z.string(),
  hours_from: num,
  hours_to: num,
  long_max_h: num,
  focus: z.string(),
});
export type Phase = z.infer<typeof Phase>;

export const PlanAdjustment = Adjustment.extend({ details: z.unknown().optional() });
export type PlanAdjustment = z.infer<typeof PlanAdjustment>;

export const Constraint = z.object({
  id: num,
  start: z.string(),
  end: z.string(),
  max_hours_per_week: nnum,
  unavailable_weekdays: z.array(num),
  note: z.string(),
});
export type Constraint = z.infer<typeof Constraint>;

export const Availability = z.object({ weekday: num, max_hours: num, fixed_session: nstr });
export type Availability = z.infer<typeof Availability>;

// GET /me/suggestions (0.9.12): what the rides suggest next to the current values ("Your rides suggest…")
export const Suggestion = z.discriminatedUnion("field", [
  z.object({
    field: z.literal("max_hr"),
    label: z.string(),
    value: num,
    current: num,
    current_typed: z.boolean(),
  }),
  z.object({
    field: z.literal("lthr"),
    label: z.string(),
    value: num,
    current: num,
    current_typed: z.boolean(),
  }),
  z.object({
    field: z.literal("week"),
    label: z.string(),
    value: z.array(Availability),
    current: z.array(Availability),
    value_hours: num,
    current_hours: num,
    ride_days: z.array(num),
  }),
  z.object({
    field: z.literal("indoor_months"),
    label: z.string(),
    value: z.array(num),
    current: z.array(num),
  }),
  z.object({ field: z.literal("trainer_app"), label: z.string(), value: z.string(), current: nstr }),
]);
export type Suggestion = z.infer<typeof Suggestion>;
export const Suggestions = z.object({
  items: z.array(Suggestion),
  based_on: z.object({ rides: num, days: num, weeks: num.optional() }),
  applied: z.array(z.string()).optional(),
});
export type Suggestions = z.infer<typeof Suggestions>;

export const Place = z.object({ id: z.string(), name: z.string(), lat: num, lon: num, is_home: z.boolean() });
export type Place = z.infer<typeof Place>;
// GET /places/search?q=: places by name (geocoding)
export const PlaceHit = z.object({ name: z.string(), lat: num, lon: num });
export type PlaceHit = z.infer<typeof PlaceHit>;

export const BodyPoint = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  measured_at: z.string(),
  /** the rider-local calendar day (one point per day per field) */
  day: z.string(),
  weight_kg: nnum,
  body_fat_pct: nnum,
  muscle_mass_kg: nnum.optional().default(null),
  bone_mass_kg: nnum.optional().default(null),
  body_water_pct: nnum.optional().default(null),
  bmi: nnum.optional().default(null),
  /** plain 7-day median per field, null on a day without a value (never bridged); `trend_algo_version` 0 */
  trend: z
    .object({
      weight_kg: nnum,
      body_fat_pct: nnum,
      muscle_mass_kg: nnum,
      bone_mass_kg: nnum,
      body_water_pct: nnum,
      bmi: nnum,
    })
    .optional(),
  trend_algo_version: num.optional(),
  source: z.string(),
});
export const BpPoint = z.object({
  id: z.union([z.string(), z.number()]),
  measured_at: z.string(),
  systolic: num,
  diastolic: num,
  pulse: nnum.optional(),
  source: z.string(),
  note: nstr.optional(),
});
const wn = nnum.optional().default(null);
const ws = nstr.optional().default(null);
export const WellnessPoint = z.object({
  day: z.string(),
  resting_hr: wn,
  min_hr: wn,
  max_hr: wn,
  hrv_ms: wn,
  hrv_weekly_ms: wn,
  hrv_baseline_low: wn,
  hrv_baseline_high: wn,
  hrv_status: ws,
  sleep_s: wn,
  sleep_score: wn,
  sleep_deep_s: wn,
  sleep_light_s: wn,
  sleep_rem_s: wn,
  sleep_awake_s: wn,
  body_battery_max: wn,
  body_battery_min: wn,
  stress_avg: wn,
  stress_max: wn,
  steps: wn,
  intensity_min_moderate: wn,
  intensity_min_vigorous: wn,
  respiration_avg: wn,
  spo2_avg: wn,
  vo2max: wn,
  vo2max_cycling: wn,
  readiness_score: wn,
  readiness_level: ws,
  training_status: ws,
  acute_load: wn,
  chronic_load: wn,
  acwr: wn,
  load_balance_feedback: ws,
  // Garmin performance extras + hydration (optional, Settings → Data sources)
  endurance_score: wn,
  endurance_class: ws,
  hill_score: wn,
  hill_strength: wn,
  hill_endurance: wn,
  heat_acclimation_pct: wn,
  altitude_acclimation_m: wn,
  water_ml: wn,
  sweat_loss_ml: wn,
  body_battery_charged: wn,
  body_battery_drained: wn,
  stress_high_s: wn,
  active_kcal: wn,
  total_kcal: wn,
  recovery_time_h: wn,
  load_aerobic_low: wn,
  load_aerobic_high: wn,
  load_anaerobic: wn,
});
// POST /health/body: the stored row (no `day`, no trend); `quality` "approximate" for a rough answer (card 016 DV-22)
export const BodyEntry = z.object({
  id: z.union([z.string(), z.number()]),
  measured_at: z.string(),
  weight_kg: nnum,
  body_fat_pct: nnum.optional().default(null),
  source: z.string(),
  quality: z.enum(["approximate"]).nullable().optional().default(null),
});
// GET /health/weight (card 016 DV-22): weight per day with how sure it is, and the gap prompt
export const WeightQuality = z.enum(["measured", "approximate", "estimated", "carried", "unknown"]);
export const WeightDay = z.object({ day: z.string(), weight_kg: nnum, quality: WeightQuality });
export const WeightPrompt = z.object({
  /** the day the answer is dated (mid-month in the middle of the open gap; today before any weigh-in) */
  day: z.string(),
  /** YYYY-MM of `day`, for "Roughly what did you weigh around <month>?" */
  month: z.string(),
  /** the last weigh-in before the gap; null before any weigh-in */
  since: nstr,
  gap_days: nnum,
});
export const WeightSeries = z.object({ days: z.array(WeightDay), prompt: WeightPrompt.nullable() });
export type WeightDay = z.infer<typeof WeightDay>;
export type WeightPrompt = z.infer<typeof WeightPrompt>;
export type WeightSeries = z.infer<typeof WeightSeries>;
export type BodyPoint = z.infer<typeof BodyPoint>;
export type BpPoint = z.infer<typeof BpPoint>;
export type WellnessPoint = z.infer<typeof WellnessPoint>;

export const Connection = z.object({
  provider: z.string(),
  label: z.string(),
  status: z.enum(["active", "error", "disabled", "not_connected"]),
  last_sync_at: nstr,
  last_error: nstr,
  capabilities: z.array(z.string()),
  syncing: z.boolean().optional().default(false),
  progress: z.record(z.string(), num).nullable().optional().default(null),
  /** a rider's first Garmin import (0.9.12): "pending" while queued or running, then "done" */
  backfill: z.string().nullable().optional().default(null),
  /** other riders' first imports that run before this one (imports go one at a time) */
  backfill_queue: num.nullable().optional().default(null),
  options: z
    .array(z.object({ key: z.string(), label: z.string(), help: z.string(), enabled: z.boolean() }))
    .optional()
    .default([]),
});
export type Connection = z.infer<typeof Connection>;

export const GarminLoginResult = z.object({
  status: z.enum(["connected", "mfa_required"]),
  login_id: z.string().optional(),
});

export const SyncStart = z.object({ status: z.enum(["started", "running"]) });
export const McpConnector = z.object({ active: z.boolean(), created_at: nstr, legacy: z.boolean() });
export const McpConnectorUrl = z.object({ url: z.string() });

// GET /system/health: the live self-check and background jobs
export const SystemHealth = z.object({
  checked_at: nstr,
  checks: z.array(
    z.object({
      check: z.string(),
      ok: z.boolean(),
      level: z.enum(["ok", "warning", "error"]),
      detail: z.string(),
    }),
  ),
  jobs: z.array(
    z.object({
      job: z.string(),
      started_at: z.string(),
      finished_at: nstr,
      ok: z.boolean(),
      summary: z.record(z.string(), z.unknown()),
      failures_7d: num,
      // MP-01: the failed runs of the last 7 days grouped by error, newest first, at most 3 (older servers: none)
      last_errors: z.array(z.object({ error: z.string(), n: num, last: z.string() })).default([]),
    }),
  ),
});
export type SystemHealth = z.infer<typeof SystemHealth>;

// GET /system/storage (owner only): sizes, backups, warnings (docs/storage.md)
export const StorageReport = z.object({
  database_mb: nnum,
  tables: z.array(z.object({ table: z.string(), rows: z.number(), mb: nnum })),
  raw_files_mb: z.number(),
  disk: z.object({ total_gb: z.number(), free_gb: z.number(), free_pct: z.number() }),
  backups: z
    .object({
      ok: z.boolean(),
      last_at: nstr,
      daily: z.number(),
      weekly: z.number(),
      monthly: z.number(),
      total_mb: z.number(),
      offsite: z.object({ ok: z.boolean(), last_at: nstr }).nullable(),
    })
    .nullable(),
  warnings: z.array(z.string()),
  retention: z.object({ route_suggestions_days: z.number(), backups: z.string() }),
});
export type StorageReport = z.infer<typeof StorageReport>;

export const Token = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  kind: z.enum(["personal", "device"]),
  created_at: z.string(),
  last_used_at: nstr,
});
export type Token = z.infer<typeof Token>;
export const NewToken = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  kind: z.enum(["personal", "device"]),
  token: z.string(),
});
export const Pairing = z.object({ token: z.string(), pair_url: z.string(), qr_svg: z.string() });
export type Pairing = z.infer<typeof Pairing>;

/** A background import (0.9.10): GET /uploads/{id}, and in the POST /uploads answer. */
export const UploadStatus = z.object({
  id: z.string(),
  filename: z.string(),
  kind: z.string(), // garmin_export | fit_zip | apple_health | fit | gpx
  size_bytes: num,
  status: z.enum(["queued", "running", "done", "failed"]),
  total: nnum, // files to import, when known (grows while a Garmin export's archives are opened)
  done: num,
  errors: num,
  counts: z.record(z.string(), num),
  message: nstr, // why it failed, in plain words
  created_at: z.string(),
  finished_at: nstr,
});
export type UploadStatus = z.infer<typeof UploadStatus>;
export const UploadList = z.object({
  items: z.array(UploadStatus),
  recalculating: z.boolean(), // loads are being recalculated after a threshold change
  notice: nstr, // e.g. "Upload your Garmin export again to add sleep, HRV and resting HR."
});
export type UploadList = z.infer<typeof UploadList>;

/** imported: what a small upload stored at once; null when the file is imported in the background (see upload). */
export const UploadResult = z.object({
  imported: z
    .looseObject({
      activities: num.optional(),
      routes: z.array(RouteSummary).optional(),
      body: num.optional(),
      blood_pressure: num.optional(),
      wellness: num.optional(),
    })
    .nullable(),
  upload: UploadStatus.nullable().optional(),
});
export type UploadResult = z.infer<typeof UploadResult>;

export const RegenerateResult = z.object({ created: num, kept: num });
export const RecomputeResult = z.object({
  recomputed_activities: num, // recalculated in the request
  recalculating: z.boolean().optional().default(false), // a long history: recalculated in the background
});
export const DayStart = z.object({ day: z.string(), place_id: nstr });

// ---------------------------------------------------------------- v1.1: events, indoor, settings
export const EVENT_FORMATS = [
  "one_day",
  "multi_day_stage",
  "ultra_unsupported",
  "brevet",
  "festival",
] as const;
export const CatalogEvent = z.object({
  id: z.string(),
  name: z.string(),
  country: z.string(),
  location: z.string(),
  lat: num,
  lon: num,
  date: z.string(),
  date_confirmed: z.boolean(),
  days: num,
  distance_km: num,
  elevation_m: nnum,
  unpaved_pct: nnum,
  format: z.enum(EVENT_FORMATS),
  supported: z.boolean(),
  url: z.string(),
  tags: z.array(z.string()),
  registered: z.boolean(),
  source_note: nstr.optional().default(null),
});
export type CatalogEvent = z.infer<typeof CatalogEvent>;

export const EventRecommendation = z.object({
  event: CatalogEvent,
  suggested_kind: z.enum(["B", "C"]),
  score: num,
  phase_at_event: nstr,
  distance_from_home_km: nnum, // null without a start place (RD-01)
  drive_hours: nnum,
  reasons: z.array(z.string()),
  warnings: z.array(z.string()),
  already_goal: z.boolean(),
});
export type EventRecommendation = z.infer<typeof EventRecommendation>;

export const Settings = z.object({
  indoor_months: z.array(num),
  indoor_weekdays_only: z.boolean(),
  trainer_app: nstr,
  amr_system_extra_kg: num.optional().default(20),
  block_weeks: z.boolean().optional().default(false),
  email_alerts: z.boolean().optional().default(true),
});
export type Settings = z.infer<typeof Settings>;

export const Onboarding = z.object({
  done: z.boolean(),
  history_weeks: num,
  rides: num,
  week: z.array(Availability),
  indoor_months: z.array(num),
  trainer_app: nstr,
  heart_rate: z.object({ max_hr: num, lthr: num, estimate: z.boolean() }).nullable(),
  presets: z.array(RacePreset),
  fixed_sports: z.record(z.string(), z.string()),
});
export type Onboarding = z.infer<typeof Onboarding>;

export const IndoorRoute = z.object({
  id: z.string(),
  world: nstr,
  name: z.string(),
  distance_km: num,
  elevation_m: nnum,
  laps: num,
  est_minutes: num,
  reason: z.string(),
});
export type IndoorRoute = z.infer<typeof IndoorRoute>;

export const Workout = z.object({
  name: z.string(),
  sport: z.string(),
  total_minutes: num,
  steps: z.array(
    z.object({ label: z.string(), duration_s: num, power_low_pct: num, power_high_pct: num, cadence: nnum }),
  ),
});
export type Workout = z.infer<typeof Workout>;

export const Indoor = z.object({
  session: PlannedSession.nullable(),
  routes: z.array(IndoorRoute),
  workout: Workout.nullable(),
});
export type Indoor = z.infer<typeof Indoor>;

// ---------------------------------------------------------------- v0.5: the coach conversation
/** Scales 1-5 where 5 is always best; RPE 1-10. */
export const CheckinBp = z.object({
  systolic: num,
  diastolic: num,
  pulse: nnum,
  measured_at: z.string(),
});
export type CheckinBp = z.infer<typeof CheckinBp>;

export const Checkin = z.object({
  id: num,
  kind: z.enum(["morning", "ride"]),
  day: z.string(),
  activity_id: nstr,
  rpe: nnum,
  legs: nnum,
  energy: nnum,
  soreness: nnum,
  motivation: nnum,
  sleep_quality: nnum,
  stomach: nnum,
  carbs_g: nnum,
  fluids_l: nnum,
  loaded: z.boolean().nullable(),
  ill: z.boolean(),
  injured: z.boolean(),
  note: z.string(),
  updated_at: nstr,
  /** Morning check-in only: the day's check-in blood-pressure reading; null without one or without the health-data consent. */
  bp: CheckinBp.nullable().optional().default(null),
});
export type Checkin = z.infer<typeof Checkin>;

export const RacePractice = z.object({
  fuelling: z.object({
    target_g_h: z.number(),
    recent_avg_g_h: nnum,
    rides: z.array(
      z.object({
        day: z.string(),
        activity_id: z.string(),
        hours: z.number(),
        carbs_g_h: nnum,
        fluids_l_h: nnum,
        stomach: nnum,
        loaded: z.boolean(),
        target_g_h: z.number(),
      }),
    ),
    note: z.string(),
  }),
  loaded: z.object({
    rides: z.number(),
    hours: z.number(),
    last_day: nstr,
    due: z.boolean(),
    note: z.string(),
  }),
});
export type RacePractice = z.infer<typeof RacePractice>;

export const SpeedModel = z
  .object({
    fitted_at: nstr,
    rides: z.number(),
    km: z.number(),
    bands: z.array(z.string()),
    rows: z.array(z.object({ surface: z.enum(["paved", "gravel", "rough"]), kmh: z.array(nnum) })),
  })
  .nullable();
export type SpeedModel = z.infer<typeof SpeedModel>;

export const PushDevice = z.object({
  id: z.number(),
  device: z.string(),
  morning_time: z.string(),
  last_sent_day: nstr,
  created_at: z.string(),
});
export type PushDevice = z.infer<typeof PushDevice>;
export const PushInfo = z.object({ public_key: z.string(), devices: z.array(PushDevice) });
export type PushInfo = z.infer<typeof PushInfo>;

export const InboxItem = z.object({
  kind: z.enum([
    "ftp_result",
    "morning_checkin",
    "ride_checkin",
    "missed_session",
    "weather_swap",
    "found_race", // "No race yet": every 28 days, "Add a race" or "Not yet"
    "data_notice", // Garmin export needed again for health data (detail says what to do)
  ]),
  title: z.string(),
  detail: z.string(),
  day: z.string(),
  activity_id: nstr,
  session_id: nnum,
  to_day: nstr.optional().default(null), // weather_swap: the day the ride would move to
  ftp: z // ftp_result: the FTP the test suggests (null without power data)
    .object({ proposed_w: nnum, best_minute_w: nnum, current_w: nnum })
    .nullable()
    .optional()
    .default(null),
});
export type InboxItem = z.infer<typeof InboxItem>;

export const MorningResult = z.object({
  checkin: Checkin,
  plan_changes: z.array(z.string()),
  readiness: Readiness,
});
export const RideCheckinResult = z.object({ checkin: Checkin, insights: Insights });

export const SkipReason = z.object({ key: SkipReasonKey, label: z.string(), effect: z.string() });
export type SkipReason = z.infer<typeof SkipReason>;
export const SkipResult = z.object({
  session: PlannedSession,
  reaction: z.string(),
  suggestion: z
    .union([
      z.object({ action: z.literal("move"), session_id: num, day: z.string() }),
      z.object({ action: z.literal("describe_route"), day: z.string() }),
    ])
    .nullable(),
});
export type SkipResult = z.infer<typeof SkipResult>;

export const RouteRequest = z.object({
  id: num,
  day: z.string(),
  text: z.string(),
  interpretation: z.string(),
  notes: z.array(z.string()),
  place: z.object({ name: z.string(), lat: num, lon: num }).nullable(),
  created_at: z.string(),
});
export type RouteRequest = z.infer<typeof RouteRequest>;
export const RouteRequestResult = z.object({ request: RouteRequest, routes: z.array(RouteSummary) });

// ---------------------------------------------------------------- v0.6: AMR preparation index (docs/amr-fitness.md)
const AmrComponents = { A: nnum, D: nnum, R: nnum, M: nnum };
/** An input the index estimated or carried that day (card 074 RF-18a; only once that formula is shown). */
const AmrEstimated = z.object({
  input: z.enum(["weight", "ftp"]),
  quality: z.string(), // weight: approximate | estimated | carried; ftp: carried
  age_days: num,
  since: z.string(), // the last real reading (ISO day)
  until: nstr, // the next one, null while the gap is open
});
export type AmrEstimated = z.infer<typeof AmrEstimated>;
export const AmrFitnessToday = z.object({ score: nnum, low: nnum, high: nnum, ...AmrComponents });
export const AmrFitness = z.object({
  current: z.object({
    day: z.string(),
    score: nnum,
    low: nnum,
    high: nnum,
    ...AmrComponents,
    inputs: z.object({
      ftp_w: nnum,
      ftp_since: nstr,
      weight_kg: nnum,
      system_extra_kg: num,
      long_h_week: num,
      pairs_week: num,
      climb_m_week: num,
      relevant_h_week: num,
      assumed: z.array(z.string()).optional().default([]),
      // RF-18a only (card 074): each input's quality and age in days
      weight_quality: z.string().optional(),
      weight_age_d: nnum.optional(),
      ftp_quality: z.string().optional(),
      ftp_age_d: nnum.optional(),
    }),
    estimated: z.array(AmrEstimated).optional(),
    explain: z.array(z.string()),
  }),
  history: z.array(
    z.object({
      day: z.string(),
      score: nnum,
      low: nnum,
      high: nnum,
      ...AmrComponents,
      assumed: z.boolean().optional().default(false), // FTP/weight taken from a later date
      inputs: z.object({ ftp_w: nnum, weight_kg: nnum }).optional(), // what is missing when A (and the score) is null
      estimated: z.array(AmrEstimated).optional(), // RF-18a only (card 074)
    }),
  ),
  method: z.object({
    race: RaceProfile.nullable().optional().default(null),
    version: z.string(),
    weights: z.object({ A: num, D: num, R: num, M: num }),
    half_life_days: num,
    window_days: num,
    references: z.record(z.string(), num),
    band: num,
    finish_line: num,
    note: z.string(),
  }),
  // Garmin's own scores as an outside check (null when not available)
  garmin: z
    .record(z.string(), z.object({ value: num, day: z.string(), change_28d: nnum }).nullable())
    .optional()
    .default({}),
  // the index to race eve if every planned ride is ridden as planned (weekly points)
  projection: z
    .array(z.object({ day: z.string(), score: nnum, ...AmrComponents }))
    .optional()
    .default([]),
});
export type AmrFitness = z.infer<typeof AmrFitness>;

// ------------------------------------------------------------------- accounts (0.9.2)
export const AuthConfig = z.object({
  google: z.boolean(),
  turnstile_site_key: nstr,
  signup: z.enum(["invite", "open", "closed"]),
});
export type AuthConfig = z.infer<typeof AuthConfig>;

const ConsentState = z.object({ granted: z.boolean(), version: z.string(), at: z.string() }).nullable();
export const Account = z.object({
  email: z.string(),
  name: z.string(),
  role: z.enum(["user", "admin"]),
  timezone: z.string(),
  sign_in: z.array(z.enum(["password", "google"])),
  consents: z.object({ health: ConsentState, ai: ConsentState, terms: ConsentState }),
  sessions: z.array(
    z.object({
      id: z.number(),
      user_agent: z.string(),
      created_at: z.string(),
      last_seen_at: z.string(),
      current: z.boolean(),
    }),
  ),
});
export type Account = z.infer<typeof Account>;

export const Invite = z.object({
  id: z.number(),
  code: z.string(),
  note: z.string(),
  max_uses: z.number(),
  uses: z.number(),
  expires_at: nstr,
  created_at: z.string(),
  /** 0.9.10: a personal invite is bound to this address (confirmed at sign-up); null = anyone */
  email: nstr,
});
export type Invite = z.infer<typeof Invite>;
/** Sign-up/sign-in/verify/reset answers. `choose_password` (verify only, 0.9.10): the link was opened in
 *  another browser than the one that signed up, so the address is confirmed and `token` is a reset token
 *  for choosing the password (/reset?token=…). */
export const AuthStatus = z.object({
  status: z.enum(["check_email", "signed_in", "choose_password"]),
  token: z.string().optional(),
});

/** GET /admin/users (0.9.10), one per account; the admin suspends, unsuspends and makes reset links. */
export const AdminUser = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.enum(["user", "admin"]),
  status: z.enum(["pending", "active", "suspended"]),
  email_confirmed: z.boolean(),
  created_at: z.string(),
  last_seen_at: nstr,
  onboarded: z.boolean(),
  /** the owner's per-rider switch for the Garmin login (0.9.12, ADR 0007) */
  garmin_login: z.enum(["allowed", "blocked"]).optional().default("allowed"),
});
export type AdminUser = z.infer<typeof AdminUser>;
export const AdminUsers = z.array(AdminUser);
/** POST /feedback (0.9.12): "Report a problem" */
export const FeedbackCreated = z.object({
  id: z.number(),
  status: z.enum(["open", "done"]),
  created_at: z.string(),
});
/** GET /admin/feedback, PUT /admin/feedback/{id} (0.9.12): riders' reports, admins only */
export const FeedbackItem = z.object({
  id: z.number(),
  user_id: z.string(),
  email: z.string(),
  text: z.string(),
  context: z.record(z.string(), z.string()),
  status: z.enum(["open", "done"]),
  created_at: z.string(),
});
export type FeedbackItem = z.infer<typeof FeedbackItem>;
export const FeedbackList = z.array(FeedbackItem);
/** GET /admin/problems (0.9.12): request errors, broken plan rules, failed background jobs */
export const Problem = z.object({
  kind: z.enum(["request", "plan_check", "job"]),
  at: z.string(),
  error_id: nstr,
  where: z.string(),
  status: nnum,
  error: z.string(),
  email: nstr,
});
export type Problem = z.infer<typeof Problem>;
export const Problems = z.object({
  days: num,
  total: num,
  by_where: z.array(z.object({ where: z.string(), count: num })),
  items: z.array(Problem),
});
/** GET /admin/users/{id}/support (0.9.12): one rider's setup, no health values, no positions */
export const RiderSupport = z.object({
  user: AdminUser,
  timezone: z.string(),
  welcome_done: z.boolean(),
  race: z
    .object({ name: z.string(), date: nstr, days_to_go: nnum, placeholder: z.boolean().optional() })
    .nullable(),
  plan: z.object({ sessions_ahead: num, last_day: nstr, started: nstr, broken_rules: nstr }),
  start_place: z.boolean(),
  activities: z.object({ count: num, last: nstr }),
  connections: z.array(
    z.object({ provider: z.string(), status: z.string(), last_sync_at: nstr, last_error: nstr }),
  ),
  uploads: z.array(
    z.object({
      filename: z.string(),
      kind: z.string(),
      status: z.string(),
      message: nstr,
      created_at: z.string(),
    }),
  ),
  consents: z.array(z.string()),
  checkins_7d: num,
  open_reports: num,
});
export type RiderSupport = z.infer<typeof RiderSupport>;
/** POST /admin/users/{id}/reset-link (0.9.10) */
export const ResetLink = z.object({ link: z.string(), expires_in_minutes: z.number() });
/** POST /account/revoke-access (0.9.10) */
export const RevokeAccess = z.object({ sessions_ended: z.number(), tokens_revoked: z.number() });

/** GET /billing (0.9.6): the plan, where it comes from, features with this month's use, checkout setup. */
export const Billing = z.object({
  plan: z.enum(["free", "amateur", "pro"]),
  source: z.enum(["beta", "admin", "subscription", "free"]),
  until: nstr,
  features: z.record(
    z.string(),
    z.object({ label: z.string(), included: z.boolean(), quota: nnum, used: num }),
  ),
  beta: z.boolean(),
  checkout: z.object({
    enabled: z.boolean(),
    environment: z.string(),
    client_token: nstr,
    prices: z.array(z.string()),
  }),
  subscriptions: z.array(
    z.object({ plan: z.string(), price_key: nstr, status: z.string(), period_end: nstr, cancel_at: nstr }),
  ),
  can_manage: z.boolean(),
});
export type Billing = z.infer<typeof Billing>;
export const CheckoutSession = z.object({
  price_id: z.string(),
  customer_id: z.string(),
  custom_data: z.object({ user_id: z.string(), sig: z.string() }),
  environment: z.string(),
  client_token: z.string(),
});
export type CheckoutSession = z.infer<typeof CheckoutSession>;

/** AI coach (0.9.7). */
export const CoachNote = z.object({ day: z.string(), text: z.string() }).nullable();
export type CoachNote = z.infer<typeof CoachNote>;
export const ChatReply = z.object({
  conversation_id: z.string(),
  reply: z.string(),
  looked_at: z.array(z.string()),
});
export type ChatReply = z.infer<typeof ChatReply>;
export const ChatMessage = z.object({
  role: z.enum(["user", "assistant"]),
  text: z.string(),
  looked_at: z.array(z.string()),
  at: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

/** Admin (0.9.8): invite requests from the landing page and the allow-listed daily counts. */
export const InviteRequest = z.object({
  id: num,
  email: z.string(),
  note: z.string(),
  status: z.enum(["new", "invited", "declined"]),
  created_at: z.string(),
  invited_at: nstr,
});
export type InviteRequest = z.infer<typeof InviteRequest>;
export const Analytics = z.object({
  events: z.record(z.string(), z.string()),
  days: z.record(z.string(), z.record(z.string(), num)),
  totals: z.record(z.string(), num),
});
export type Analytics = z.infer<typeof Analytics>;
